import { createContext } from "react";
import type { PartnerMeResponse } from "@dearbill/shared";

/**
 * Shared `GET /api/partner/me` record for the authenticated partner app.
 * `PartnerMeProvider` fetches it once for every authenticated route, and the
 * top bar, the org list, the profile page and the org tabs read it without
 * refetching. A name edit on `/profile` lifts the new record through `setMe`,
 * so the top bar updates immediately.
 *
 * `me` is `null` until the fetch resolves, and stays `null` if it fails.
 * `settled` flips once the fetch resolves or fails, so consumers can tell
 * "still loading" from "loaded but empty". `error` is true only on a non-401
 * failure; `useApi` handles a 401 by clearing the session.
 */
export interface PartnerMeContextValue {
  /** The caller's account record, or `null` while loading / after a failure. */
  me: PartnerMeResponse | null;
  /** Lift the server's updated `me` record (after editing your own name). */
  setMe: (next: PartnerMeResponse) => void;
  /** `true` once the `/me` fetch has resolved or failed. */
  settled: boolean;
  /** `true` if the `/me` fetch failed with a non-401 error; consumers may show an error. */
  error: boolean;
}

export const PartnerMeContext = createContext<PartnerMeContextValue | null>(null);

export const PartnerMeContextProvider = PartnerMeContext.Provider;
